import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import TypeableCombo from "./TypeableCombo";
import { runAdmissionsSave } from "./orchestrator";
import {
  loadStudentPrefixes,
  fetchNextStudentNumber,
} from "../directory/StudentProfileForm";
import s from "./AdmissionsPage.module.css";

/**
 * Warn the user before they nav away or refresh with unsaved wizard data.
 *
 * Two channels:
 *   - `beforeunload` covers refresh, tab close, typing a new URL. Browsers
 *     show their own generic dialog; the text we return is ignored on modern
 *     browsers but setting `returnValue` is what actually triggers the prompt.
 *   - `history.pushState` wrap covers in-app nav (Topbar link clicks). React
 *     Router's useBlocker would be cleaner, but it requires a data router
 *     (createBrowserRouter / RouterProvider); this app is on <BrowserRouter>
 *     so we reach one level deeper and intercept the underlying API.
 */
function useUnsavedChangesGuard(when, message) {
  useEffect(() => {
    if (!when) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = message; return message; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [when, message]);

  useEffect(() => {
    if (!when) return;
    // Intercept React Router's programmatic navigation. Link clicks, useNavigate
    // calls, and <Navigate> all funnel through history.pushState — prompting
    // here catches the lot without needing to walk the DOM for anchor clicks.
    const origPush    = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      if (!window.confirm(message)) return;
      return origPush.apply(window.history, args);
    };
    window.history.replaceState = function (...args) {
      if (!window.confirm(message)) return;
      return origReplace.apply(window.history, args);
    };
    return () => {
      window.history.pushState    = origPush;
      window.history.replaceState = origReplace;
    };
  }, [when, message]);
}

/**
 * Unified admissions wizard — one form that creates a student, their
 * parents, enrollment, fee structure, discounts, and attached documents.
 * On save, the orchestrator calls each existing tab's POST endpoint in
 * dependency order — exactly as if an admin had clicked through every
 * tab by hand.
 */

const STEPS = [
  { key: "student",    label: "Student",             short: "Student" },
  { key: "parents",    label: "Parents / Guardians", short: "Parents" },
  { key: "enrollment", label: "Enrollment",          short: "Enrollment" },
  { key: "tuition",    label: "Tuition",             short: "Tuition" },
  { key: "discounts",  label: "Discounts",           short: "Discounts" },
  { key: "documents",  label: "Documents",           short: "Documents" },
  { key: "review",     label: "Review & Save",       short: "Review" },
];

const BLANK_STUDENT = {
  studentNumber: "", firstName: "", middleName: "", lastName: "",
  birthdate: "", sex: "", contact: "", address: "",
};
const BLANK_PARENT_ROW = {
  existingId: null, firstName: "", lastName: "",
  contact: "", email: "", occupation: "", address: "",
  relationship: "Guardian", primary: false,
};
const BLANK_ENROLLMENT = {
  schoolYear: null, semester: null, gradeLevel: null, section: null,
};
const BLANK_TUITION = {
  existingId: null, isNew: false,
  tuitionFee: "", miscFee: "", booksFee: "",
  enrollmentFee: "", months: 9, breakdown: "",
};
const BLANK_DISCOUNT_ROW = {
  type: "SIBLING", percentage: "", notes: "", customName: "",
  appliesTuition: true, appliesMisc: false, appliesBooks: false,
};
const BLANK_DOC_ROW      = { label: "", file: null };

const DISCOUNT_TYPES = [
  ["PIONEER", "Pioneer"],       ["HONORS", "With Honors"],
  ["HIGH_HONORS", "With High Honors"], ["SIBLING", "Sibling"],
  ["REFERRAL", "Referral"],     ["FULL_PAYMENT", "Full Payment"],
  ["INVESTOR", "Investor"],     ["SHAREHOLDER", "Shareholder"],
  ["OTHERS", "Others (one-off)"],
];
const RELATIONSHIPS = ["Father", "Mother", "Guardian", "Grandparent", "Uncle", "Aunt", "Other"];

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
function authHeaders() {
  const u = getUser();
  return { "Content-Type": "application/json",
           "X-User-Role":  u?.role || "",
           "X-Username":   u?.username || "" };
}

export default function AdmissionsPage() {
  const navigate = useNavigate();
  // Post-save shortcuts to Tuition / SoA are admin-only. Registrar owns
  // admissions; tuition + payments belong to Cashier, so those roles get
  // the clean wizard without cross-surface jumps.
  const currentRole = getUser()?.role;
  const canJumpToFinance = currentRole === "Admin" || currentRole === "SuperAdmin";
  // ── Wizard state ────────────────────────────────────────────────
  const [stepIndex, setStepIndex] = useState(0);
  const [student,   setStudent]   = useState({ ...BLANK_STUDENT });
  const [parents,   setParents]   = useState([]);
  const [enrollment, setEnrollment] = useState({ ...BLANK_ENROLLMENT });
  const [tuition,   setTuition]   = useState({ ...BLANK_TUITION });
  const [discounts, setDiscounts] = useState([]);
  const [documents, setDocuments] = useState([]);

  // ── Save state ──────────────────────────────────────────────────
  // `created` holds every id produced by a successful step — passed
  // back into the orchestrator on retry so we don't double-create
  // after a partial failure.
  const [created, setCreated] = useState({});
  // Per-step progress for the Review step's progress UI.
  const [progress, setProgress] = useState({});
  const [err, setErr]   = useState("");
  const [ok,  setOk]    = useState("");
  const [busy, setBusy] = useState(false);
  // Flipped true after a fully-successful save so the unsaved-changes
  // guard stops prompting. We don't flip it back on further edits —
  // after a successful admission the wizard is effectively a new blank
  // anyway; the admin typically closes the page.
  const [savedCleanly, setSavedCleanly] = useState(false);

  // ── Student-number prefix + auto-increment ──────────────────────
  // Prefix list is managed in Settings (and shared via localStorage).
  // The active prefix drives an auto-fetched next number so the admin
  // gets "S-2026-00042" pre-filled as soon as the page loads.
  const [studentPrefixes, setStudentPrefixes] = useState(() => loadStudentPrefixes([]));
  const [activePrefix, setActivePrefix]       = useState(() => loadStudentPrefixes([])[0] || "");
  // Track what we auto-filled so the isDirty guard doesn't prompt when
  // the only "change" is the auto-populated student number.
  const autoFilledNumberRef = useRef("");

  // ── Unsaved-changes guard ──────────────────────────────────────
  // Dirty = the admin has typed anything into the wizard AND hasn't
  // saved successfully yet. Guard fires on nav / refresh / close.
  const isDirty = useMemo(() => {
    if (savedCleanly) return false;
    if (busy) return true;  // never let nav interrupt an in-flight save
    // Auto-filled student number doesn't count as user input.
    const numberTouched = !!student.studentNumber
                       && student.studentNumber !== autoFilledNumberRef.current;
    const studentTouched =
      numberTouched || !!student.firstName || !!student.lastName ||
      !!student.middleName || !!student.birthdate || !!student.sex ||
      !!student.contact || !!student.address;
    const enrollmentTouched =
      !!(enrollment.schoolYear || enrollment.semester
         || enrollment.gradeLevel || enrollment.section);
    const tuitionTouched =
      !!(tuition.existingId || tuition.isNew);
    return studentTouched || enrollmentTouched || tuitionTouched
        || parents.length > 0 || discounts.length > 0 || documents.length > 0;
  }, [savedCleanly, busy, student, enrollment, tuition, parents, discounts, documents]);

  useUnsavedChangesGuard(
    isDirty,
    "You have unsaved admissions data. Leave this page and lose what you've entered?"
  );

  // ── Lookups ─────────────────────────────────────────────────────
  const [allParents, setAllParents]         = useState([]);
  const [schoolYears, setSchoolYears]       = useState([]);
  const [semesters, setSemesters]           = useState([]);
  const [gradeLevels, setGradeLevels]       = useState([]);
  const [sections, setSections]             = useState([]);
  const [rooms, setRooms]                   = useState([]);
  const [feeStructures, setFeeStructures]   = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/parents`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/fee-structures`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
    ]).then(([par, sy, sem, gl, sec, fs, rms]) => {
      setAllParents(Array.isArray(par)   ? par : []);
      setSchoolYears(Array.isArray(sy)   ? sy  : []);
      setSemesters(Array.isArray(sem)    ? sem : []);
      setGradeLevels(Array.isArray(gl)   ? gl  : []);
      setSections(Array.isArray(sec)     ? sec : []);
      setRooms(Array.isArray(rms)        ? rms : []);
      setFeeStructures(Array.isArray(fs) ? fs  : []);
      // Reconcile the prefix list with the freshly-loaded SY — only matters
      // on a cold install where Settings hasn't saved any prefixes yet and
      // we want the default to derive from the active SY.
      const prefixes = loadStudentPrefixes(Array.isArray(sy) ? sy : []);
      setStudentPrefixes(prefixes);
      setActivePrefix(prev => prev || prefixes[0] || "");
    });
  }, []);

  // Auto-fetch the next student number whenever the active prefix changes.
  // The admin can still type over the suggested tail by hand — we only
  // refetch on prefix changes, never on the user's keystrokes. If the
  // admin has hand-edited the field since the last auto-fill, preserve
  // their value.
  useEffect(() => {
    if (!activePrefix) return;
    let cancelled = false;
    (async () => {
      const next = await fetchNextStudentNumber(API_BASE, activePrefix);
      if (cancelled) return;
      const suggested = next || activePrefix;
      setStudent(prev => {
        const prevNum = prev.studentNumber || "";
        const wasAutoFilled = prevNum === "" || prevNum === autoFilledNumberRef.current;
        autoFilledNumberRef.current = suggested;
        return wasAutoFilled ? { ...prev, studentNumber: suggested } : prev;
      });
    })();
    return () => { cancelled = true; };
  }, [activePrefix]);

  // ── Derived lookup data ────────────────────────────────────────
  const schoolYearItems = useMemo(
    () => schoolYears.map(y => ({ id: y.id, label: y.label + (y.active ? " · Active" : "") })),
    [schoolYears]);
  /** Semesters narrowed to the picked SY — only meaningful if SY already
   *  exists (newly-typed SY has no semesters yet, so the list is empty). */
  const semesterItems = useMemo(() => {
    if (!enrollment.schoolYear?.id) return [];
    return semesters
      .filter(x => String(x.schoolYearId) === String(enrollment.schoolYear.id))
      .map(x => ({ id: x.id, label: x.label + (x.active ? " · Active" : "") }));
  }, [semesters, enrollment.schoolYear]);
  const gradeLevelItems = useMemo(
    () => gradeLevels.slice()
          .sort((a, b) => (a.ordering ?? 9999) - (b.ordering ?? 9999))
          .map(g => ({ id: g.id, label: g.name })),
    [gradeLevels]);
  /** Sections narrowed to the picked SY + GL. Same caveat as semesters —
   *  if either is brand-new, there are no existing sections to choose. */
  const sectionItems = useMemo(() => {
    const syId = enrollment.schoolYear?.id;
    const glId = enrollment.gradeLevel?.id;
    if (!syId || !glId) return [];
    return sections
      .filter(x => String(x.schoolYearId) === String(syId)
                && String(x.gradeLevelId) === String(glId))
      .map(x => ({ id: x.id, label: x.name }));
  }, [sections, enrollment.schoolYear, enrollment.gradeLevel]);
  /** Fee structures that match the picked grade + SY — what the Tuition
   *  step offers as "pick existing". If none match, the admin creates one. */
  const matchingFeeStructures = useMemo(() => {
    const syId = enrollment.schoolYear?.id;
    const glId = enrollment.gradeLevel?.id;
    if (!syId || !glId) return [];
    return feeStructures.filter(fs =>
      String(fs.schoolYearId) === String(syId) &&
      String(fs.gradeLevelId) === String(glId));
  }, [feeStructures, enrollment.schoolYear, enrollment.gradeLevel]);

  // ── Validation per step ────────────────────────────────────────
  function validateStep(k) {
    if (k === "student") {
      if (!student.studentNumber.trim()) return "Student number is required.";
      if (!student.firstName.trim())     return "First name is required.";
      if (!student.lastName.trim())      return "Last name is required.";
      if (!student.birthdate)            return "Birthdate is required.";
      if (!student.sex)                  return "Sex is required.";
      if (!student.contact.trim())       return "Contact is required.";
      if (!student.address.trim())       return "Address is required.";
    }
    if (k === "parents") {
      for (const [i, p] of parents.entries()) {
        if (p.existingId) continue;
        if (!p.firstName?.trim() || !p.lastName?.trim()) {
          return `Parent #${i + 1}: first and last name are required (or pick an existing parent).`;
        }
      }
    }
    if (k === "enrollment") {
      if (!enrollment.schoolYear) return "School year is required.";
      if (!enrollment.semester)   return "Semester is required.";
      if (!enrollment.gradeLevel) return "Grade level is required.";
      if (!enrollment.section)    return "Section is required.";
      // TypeableCombo emits { newName: "" } when the user picks "+ Create new"
      // but hasn't typed a name yet — that's still "not set" as far as save
      // is concerned.
      const unnamed = Object.entries(enrollment)
        .filter(([, v]) => v && v.newName != null && !String(v.newName).trim())
        .map(([k2]) => k2);
      if (unnamed.length) {
        const pretty = { schoolYear: "School year", semester: "Semester",
                         gradeLevel: "Grade level", section: "Section" };
        return `Type a name for: ${unnamed.map(x => pretty[x] || x).join(", ")}.`;
      }
    }
    if (k === "tuition") {
      if (!tuition.existingId && !tuition.isNew) {
        return "Pick an existing fee structure or create a new one.";
      }
    }
    if (k === "discounts") {
      for (const [i, d] of discounts.entries()) {
        const pct = Number(d.percentage);
        if (!Number.isFinite(pct) || pct <= 0) {
          return `Discount #${i + 1}: percentage must be greater than 0.`;
        }
        if (!d.appliesTuition && !d.appliesMisc && !d.appliesBooks) {
          return `Discount #${i + 1}: pick at least one fee it applies to (Tuition / Misc / Books).`;
        }
        if (d.type === "OTHERS" && !d.customName?.trim()) {
          return `Discount #${i + 1}: "Others" needs a custom name (e.g. "Early Bird").`;
        }
      }
    }
    return "";
  }

  /** Run every step's validation at save time so we never POST partial
   *  data to the backend — the user gets a clean field-level error instead
   *  of a raw Spring stack trace. Returns the first blocking error, jumps
   *  the wizard to that step so the fix is obvious. */
  function validateAllAndJump() {
    for (let i = 0; i < STEPS.length; i++) {
      const k = STEPS[i].key;
      if (k === "review") continue;
      const v = validateStep(k);
      if (v) { setStepIndex(i); return v; }
    }
    return "";
  }

  const currentStepError = validateStep(STEPS[stepIndex].key);

  /** Stepper clickability: a target step is reachable only if every earlier
   *  step passes validation. Backward nav to a completed step stays free;
   *  forward jumps past an incomplete step are blocked. Skip "review" in
   *  the prior-steps check — it has nothing to validate itself. */
  function canJumpTo(targetIndex) {
    if (targetIndex <= stepIndex) return true;  // backward always allowed
    for (let i = 0; i < targetIndex; i++) {
      const k = STEPS[i].key;
      if (k === "review") continue;
      if (validateStep(k)) return false;
    }
    return true;
  }
  /** "Done" now means "this step validates clean" — so the green ✓ only
   *  appears for steps the user has actually filled, not just for any
   *  index lower than current. */
  function isStepDone(i) {
    if (i === stepIndex) return false;
    const k = STEPS[i].key;
    if (k === "review") return false;
    return !validateStep(k);
  }

  function goTo(i)  { setErr(""); setOk(""); setStepIndex(Math.max(0, Math.min(STEPS.length - 1, i))); }
  function handleNext() {
    const v = validateStep(STEPS[stepIndex].key);
    if (v) { setErr(v); return; }
    goTo(stepIndex + 1);
  }

  // ── Save orchestration ─────────────────────────────────────────
  async function runSave() {
    // Guard: run every step's validation before we hit the backend. If
    // anything's missing, jump to that step and show a clean field-level
    // error — no Spring stack trace in the UI.
    const missing = validateAllAndJump();
    if (missing) { setErr(missing); setOk(""); return; }
    setBusy(true); setErr(""); setOk("");
    // Reset per-step progress for this attempt (but keep `created` so
    // steps that already succeeded in a prior attempt are skipped).
    const initial = { academic: "pending", tuition: "pending", student: "pending",
                      parents: "pending", enrollment: "pending",
                      discounts: "pending", documents: "pending" };
    setProgress(initial);
    try {
      const { created: out } = await runAdmissionsSave({
        student, parents, enrollment, tuition, discounts, documents,
        existing: { schoolYearItems, semesterItems, gradeLevelItems, sectionItems },
        prior: created,
        progress: (step, status) => setProgress(prev => ({ ...prev, [step]: status })),
      });
      setCreated(out);
      setSavedCleanly(true);
      setOk(`Saved. Student #${out.studentId} enrolled. Records appear in Students, Parents, Enrollments, Tuition, Discounts, and Documents tabs.`);
    } catch (e) {
      setErr(e.message || "Save failed. Fix the issue and click Save again — we'll pick up where we left off.");
    } finally {
      setBusy(false);
    }
  }

  async function runSaveAndDownload() {
    await runSave();
    try {
      const payload = buildPdfPayload({
        student, parents, enrollment, tuition, discounts, documents,
        allParents, schoolYears, semesters, gradeLevels, sections,
      });
      const res = await fetch(`${API_BASE}/api/admissions/pdf`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`PDF failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Admissions-${student.lastName || "form"}-${student.firstName || ""}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); }
  }

  const step = STEPS[stepIndex];

  return (
    <div className={s.page}>
      <h1 className={s.title}>Admissions</h1>
      <p className={s.sub}>
        Enrol a new student end-to-end in one form. On save, records flow
        into Students, Parents, Enrollments, Tuition, Discounts, and
        Documents tabs — as if you had filled out each tab yourself.
      </p>

      <div className={s.stepper}>
        {STEPS.map((st, i) => {
          const active    = i === stepIndex;
          const done      = isStepDone(i);
          const reachable = canJumpTo(i);
          return (
            <button key={st.key}
                    type="button"
                    className={`${s.step} ${active ? s.stepActive : ""} ${done ? s.stepDone : ""}`}
                    onClick={() => { if (reachable) goTo(i); }}
                    disabled={!reachable}
                    title={reachable ? "" : "Fill required fields in earlier steps first"}
                    style={reachable ? undefined : { opacity: 0.5, cursor: "not-allowed" }}>
              <span className={s.stepNum}>{done ? "✓" : (i + 1)}</span>
              <span className={s.stepLabel}>{st.short}</span>
            </button>
          );
        })}
      </div>

      <div className={s.panel}>
        <div className={s.panelHead}>
          <div>
            <h2 className={s.panelTitle}>Step {stepIndex + 1} · {step.label}</h2>
            <p className={s.panelSub}>{stepSubtitle(step.key)}</p>
          </div>
        </div>

        {err && <div className={s.err}>{err}</div>}
        {ok  && <div className={s.ok}>{ok}</div>}

        {step.key === "student" && (
          <StudentStep
            value={student} onChange={setStudent} s={s}
            prefixes={studentPrefixes}
            activePrefix={activePrefix}
            onPickPrefix={setActivePrefix}
          />
        )}

        {step.key === "parents" && (
          <ParentsStep value={parents} onChange={setParents} allParents={allParents} s={s} />
        )}

        {step.key === "enrollment" && (
          <EnrollmentStep
            value={enrollment} onChange={setEnrollment}
            schoolYearItems={schoolYearItems}
            semesterItems={semesterItems}
            gradeLevelItems={gradeLevelItems}
            sectionItems={sectionItems}
            sectionsRaw={sections}
            rooms={rooms}
            s={s}
          />
        )}

        {step.key === "tuition" && (
          <TuitionStep
            value={tuition} onChange={setTuition}
            matching={matchingFeeStructures}
            enrollment={enrollment}
            s={s}
          />
        )}

        {step.key === "discounts" && (
          <DiscountsStep
            value={discounts} onChange={setDiscounts} s={s}
            canEdit={getUser()?.role === "Admin" || getUser()?.role === "SuperAdmin"}
          />
        )}

        {step.key === "documents" && (
          <DocumentsStep value={documents} onChange={setDocuments} s={s} />
        )}

        {step.key === "review" && (
          <ReviewStep
            student={student} parents={parents}
            enrollment={enrollment} tuition={tuition}
            discounts={discounts} documents={documents}
            allParents={allParents} schoolYears={schoolYears}
            semesters={semesters} gradeLevels={gradeLevels}
            sections={sections} feeStructures={feeStructures}
            progress={progress} busy={busy}
            s={s}
          />
        )}

        <div className={s.foot}>
          <div className={s.footLeft}>
            {stepIndex > 0 && (
              <button type="button" className={s.btnGhost}
                      onClick={() => goTo(stepIndex - 1)} disabled={busy}>
                ← Back
              </button>
            )}
          </div>
          <div className={s.footRight}>
            {step.key === "review" ? (
              <>
                {/* Post-save shortcuts to Tuition / SoA — admin-only, since
                 *  registrars who own admissions don't cross into the cashier's
                 *  tuition surface. Seeding each page via URL means the admin
                 *  lands already pointed at this student. */}
                {canJumpToFinance && created.studentId && (
                  <>
                    <button type="button" className={s.btnGhost} disabled={busy}
                            onClick={() => navigate(`/admin/tuition?search=${encodeURIComponent(student.studentNumber || "")}`)}>
                      View Tuition →
                    </button>
                    <button type="button" className={s.btnGhost} disabled={busy}
                            onClick={() => navigate(`/tuition/soa?studentId=${created.studentId}`)}>
                      View Statement of Account →
                    </button>
                  </>
                )}
                <button type="button" className={s.btnGhost}
                        onClick={runSaveAndDownload} disabled={busy}>
                  {busy ? "Working…" : "Save + Download PDF"}
                </button>
                <button type="button" className={s.btnAccent}
                        onClick={runSave} disabled={busy}>
                  {busy ? "Saving…" : (created.studentId ? "Retry save" : "Save")}
                </button>
              </>
            ) : (
              <button type="button" className={s.btnPrimary}
                      onClick={handleNext}
                      disabled={busy || !!currentStepError}
                      title={currentStepError || ""}>
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function stepSubtitle(k) {
  switch (k) {
    case "student":    return "Core identity of the student being enrolled.";
    case "parents":    return "Pick existing parents or add new ones. Multiple allowed.";
    case "enrollment": return "School year, semester, grade level, section. Type a name to create new.";
    case "tuition":    return "Fee structure for this grade and school year.";
    case "discounts":  return "Scholarships / sibling / honors / etc. Optional, multiple rows.";
    case "documents":  return "Upload birth certificate, Form 137, IDs, etc. to AWS S3.";
    case "review":     return "Verify everything, then save or export as PDF.";
    default: return "";
  }
}

// ────────────────────────────────────────────────────────────────
// Step 1 · Student
// ────────────────────────────────────────────────────────────────
function StudentStep({ value, onChange, s, prefixes, activePrefix, onPickPrefix }) {
  function set(k, v) { onChange({ ...value, [k]: v }); }

  // Split the stored studentNumber into prefix + tail purely for display.
  // Whole value is the ground truth — the tail input rebuilds it as the
  // active prefix concatenated with whatever the admin typed.
  const tail = value.studentNumber?.startsWith(activePrefix)
    ? value.studentNumber.slice(activePrefix.length)
    : (value.studentNumber || "");

  return (
    <div className={s.formGrid}>
      <div className={s.field}>
        <label className={s.fieldLabel}>Student #<span className={s.req}>*</span></label>
        <div style={{ display: "flex", gap: 8 }}>
          <select className={s.fieldSelect}
                  style={{ flex: "0 0 auto", minWidth: 120 }}
                  value={activePrefix}
                  onChange={e => onPickPrefix(e.target.value)}>
            {prefixes.length === 0 && <option value="">—</option>}
            {prefixes.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className={s.fieldInput}
                 style={{ flex: 1, background: "#f5f0e4", cursor: "not-allowed" }}
                 value={tail}
                 readOnly
                 tabIndex={-1}
                 title="Auto-incremented from the server for the selected prefix"
                 placeholder="00001" />
        </div>
      </div>
      <Field s={s} label="Birthdate" type="date" required
             value={value.birthdate} onChange={v => set("birthdate", v)} />
      <Field s={s} label="First name" required
             value={value.firstName} onChange={v => set("firstName", v)} />
      <Field s={s} label="Middle name"
             value={value.middleName} onChange={v => set("middleName", v)} />
      <Field s={s} label="Last name" required
             value={value.lastName} onChange={v => set("lastName", v)} />
      <Field s={s} label="Sex" type="select" required
             value={value.sex} onChange={v => set("sex", v)}
             options={[["", "—"], ["Male", "Male"], ["Female", "Female"]]} />
      <Field s={s} label="Contact" required
             value={value.contact} onChange={v => set("contact", v)} />
      <Field s={s} label="Address" wide required
             value={value.address} onChange={v => set("address", v)} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Step 2 · Parents / Guardians
// Each row can either reference an existing parent (dropdown) OR
// carry new-parent fields inline. On save, the orchestrator POSTs
// new parents and creates parent-student links for all of them.
// ────────────────────────────────────────────────────────────────
function ParentsStep({ value, onChange, allParents, s }) {
  function addRow()         { onChange([...value, { ...BLANK_PARENT_ROW }]); }
  function removeRow(i)     { onChange(value.filter((_, idx) => idx !== i)); }
  function patchRow(i, p)   { onChange(value.map((r, idx) => (idx === i ? { ...r, ...p } : r))); }

  return (
    <div>
      {value.length === 0 && (
        <div className={s.stub} style={{ marginBottom: 14 }}>
          <div className={s.stubTitle}>No parents yet</div>
          <div>Click &quot;+ Add parent / guardian&quot; to add one. Multiple parents allowed.</div>
        </div>
      )}
      {value.map((p, i) => {
        const existing = allParents.find(x => x.id === p.existingId);
        return (
          <div key={i} className={s.reviewCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Parent / Guardian #{i + 1}</h3>
              <button type="button" className={s.btnGhost}
                      onClick={() => removeRow(i)}>Remove</button>
            </div>
            <div className={s.formGrid}>
              <div className={`${s.field} ${s.fieldWide}`}>
                <label className={s.fieldLabel}>Pick existing parent (optional)</label>
                <select className={s.fieldSelect}
                        value={p.existingId ?? ""}
                        onChange={e => {
                          const id = e.target.value ? Number(e.target.value) : null;
                          const ex = id ? allParents.find(x => x.id === id) : null;
                          patchRow(i, {
                            existingId: id,
                            firstName:  ex?.firstName  || "",
                            lastName:   ex?.lastName   || "",
                            contact:    ex?.contact    || "",
                            email:      ex?.email      || "",
                            occupation: ex?.occupation || "",
                          });
                        }}>
                  <option value="">— new parent —</option>
                  {allParents.map(x => (
                    <option key={x.id} value={x.id}>
                      {x.lastName}, {x.firstName}{x.contact ? ` · ${x.contact}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <Field s={s} label="First name" required={!p.existingId}
                     value={p.firstName} onChange={v => patchRow(i, { firstName: v })}
                     disabled={!!p.existingId} />
              <Field s={s} label="Last name" required={!p.existingId}
                     value={p.lastName} onChange={v => patchRow(i, { lastName: v })}
                     disabled={!!p.existingId} />
              <Field s={s} label="Contact"
                     value={p.contact} onChange={v => patchRow(i, { contact: v })}
                     disabled={!!p.existingId} />
              <Field s={s} label="Email"
                     value={p.email} onChange={v => patchRow(i, { email: v })}
                     disabled={!!p.existingId} />
              <Field s={s} label="Occupation"
                     value={p.occupation} onChange={v => patchRow(i, { occupation: v })}
                     disabled={!!p.existingId} />

              <Field s={s} label="Relationship" type="select"
                     value={p.relationship} onChange={v => patchRow(i, { relationship: v })}
                     options={RELATIONSHIPS.map(r => [r, r])} />
              <div className={s.field}>
                <label className={s.fieldLabel}>Primary contact?</label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 2px" }}>
                  <input type="checkbox"
                         checked={!!p.primary}
                         onChange={e => patchRow(i, { primary: e.target.checked })} />
                  <span>Yes, contact this person first</span>
                </label>
              </div>
            </div>
            {existing && (
              <div style={{ marginTop: 8, fontSize: "0.8rem", color: "#8a8070", fontStyle: "italic" }}>
                Linking to existing parent #{existing.id}. Contact info above is read-only.
              </div>
            )}
          </div>
        );
      })}
      <button type="button" className={s.btnGhost} onClick={addRow}>
        + Add parent / guardian
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Step 3 · Enrollment (typeable combos — create-on-save if new)
// ────────────────────────────────────────────────────────────────
function EnrollmentStep({
  value, onChange,
  schoolYearItems, semesterItems, gradeLevelItems, sectionItems,
  sectionsRaw, rooms,
  s,
}) {
  function set(k, v) { onChange({ ...value, [k]: v }); }
  // If SY or GL changes, reset dependent combos so we don't leak a
  // semester/section from the prior choice into the new tree.
  function setSy(v)   { onChange({ ...value, schoolYear: v, semester: null, section: null }); }
  function setGl(v)   { onChange({ ...value, gradeLevel: v, section: null }); }

  // Semester and Section dropdowns show an empty existing-items list when
  // their parent SY/GL is brand-new (no children exist yet for a not-yet-
  // created parent) — but the combo still offers "+ Create new" so the
  // admin can type names for those too. Orchestrator wires up the ids in
  // the correct order on save.
  const syTypedNew = value.schoolYear?.newName != null;
  const glTypedNew = value.gradeLevel?.newName != null;

  // Resolve the selected section's homeroom for the read-only hint below.
  // Only meaningful when an EXISTING section is picked (not a typed-new
  // one) — for typed-new sections, the homeroom isn't decided yet.
  const pickedSection = value.section?.id
    ? (sectionsRaw || []).find(x => String(x.id) === String(value.section.id))
    : null;
  const pickedHomeroom = pickedSection?.homeroomId
    ? (rooms || []).find(r => String(r.id) === String(pickedSection.homeroomId))
    : null;

  return (
    <div className={s.formGrid}>
      <TypeableCombo s={s} label="School year" required
                     value={value.schoolYear} onChange={setSy}
                     items={schoolYearItems}
                     newPlaceholder="e.g. 2026-2027" />
      <TypeableCombo s={s} label="Semester" required
                     value={value.semester} onChange={v => set("semester", v)}
                     items={semesterItems}
                     newPlaceholder="e.g. 1st Semester"
                     disabled={!value.schoolYear} />
      <TypeableCombo s={s} label="Grade level" required
                     value={value.gradeLevel} onChange={setGl}
                     items={gradeLevelItems}
                     newPlaceholder="e.g. Grade 5" />
      <TypeableCombo s={s} label="Section" required
                     value={value.section} onChange={v => set("section", v)}
                     items={sectionItems}
                     newPlaceholder="e.g. St. Ignatius of Loyola"
                     disabled={!value.schoolYear || !value.gradeLevel} />
      {(syTypedNew || glTypedNew) && (
        <div className={`${s.field} ${s.fieldWide}`} style={{ fontSize: "0.82rem", color: "#8a6d28" }}>
          New {syTypedNew && glTypedNew ? "school year and grade level" : syTypedNew ? "school year" : "grade level"}{" "}
          will be created on save. {syTypedNew ? "Semester " : ""}
          {syTypedNew && glTypedNew ? "and " : ""}
          {glTypedNew ? "Section " : syTypedNew ? "and Section " : ""}
          dropdown{(syTypedNew && glTypedNew) || (syTypedNew && !glTypedNew && value.schoolYear) ? "s" : ""} will show an empty existing list —
          use &quot;+ Create new&quot; there too.
        </div>
      )}

      {/* Homeroom inheritance hint — read-only. Surfaces *which* room the
          student will inherit by joining this section. Editing the homeroom
          itself happens on the Sections tab so per-student overrides can't
          accidentally drift the section's primary classroom. */}
      {pickedSection && (
        <div
          className={`${s.field} ${s.fieldWide}`}
          style={{
            fontSize: "0.84rem",
            color: pickedHomeroom ? "#1e6f3e" : "#8a6d28",
            background: pickedHomeroom ? "#e8f6ed" : "#fdf3d4",
            border: `1px solid ${pickedHomeroom ? "#cde9d5" : "#f0e2b1"}`,
            borderRadius: 10,
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}>
          <span style={{
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            opacity: 0.75,
          }}>Homeroom</span>
          {pickedHomeroom ? (
            <span style={{ fontWeight: 700 }}>
              {pickedHomeroom.name}
              {pickedHomeroom.building && (
                <span style={{ fontWeight: 500, opacity: 0.75 }}>
                  {" · "}{pickedHomeroom.building}
                  {pickedHomeroom.floor ? `, Floor ${pickedHomeroom.floor}` : ""}
                </span>
              )}
            </span>
          ) : (
            <span>
              No homeroom set on this section yet — the admin can assign one
              from the <b>Sections</b> tab.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Step 4 · Tuition (pick existing fee structure OR create new)
// ────────────────────────────────────────────────────────────────
function TuitionStep({ value, onChange, matching, enrollment, s }) {
  const glPicked = !!enrollment.gradeLevel;
  const syPicked = !!enrollment.schoolYear;
  const glIsNew = enrollment.gradeLevel?.newName != null;
  const syIsNew = enrollment.schoolYear?.newName != null;
  const forceNew = glIsNew || syIsNew;  // Can't pick existing if either side is new

  function setMode(mode) {
    if (mode === "existing") onChange({ ...BLANK_TUITION, existingId: value.existingId, isNew: false });
    else                     onChange({ ...BLANK_TUITION, isNew: true });
  }
  function set(k, v) { onChange({ ...value, [k]: v }); }

  // Derived total — same formula as the Fee Structures tab.
  const n = (x) => (x === "" || x == null ? 0 : Number(x) || 0);
  const total = (n(value.tuitionFee) + n(value.miscFee) + n(value.booksFee)).toFixed(2);

  if (!glPicked || !syPicked) {
    return (
      <div className={s.stub}>
        <div className={s.stubTitle}>Pick enrollment first</div>
        <div>Go back to Step 3 and choose a school year and grade level.
             Tuition depends on those two.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button type="button"
                className={value.isNew ? s.btnGhost : s.btnPrimary}
                onClick={() => setMode("existing")}
                disabled={forceNew}>
          Pick existing
        </button>
        <button type="button"
                className={value.isNew ? s.btnPrimary : s.btnGhost}
                onClick={() => setMode("new")}>
          Create new
        </button>
        {forceNew && (
          <span style={{ fontSize: "0.82rem", color: "#8a6d28", alignSelf: "center" }}>
            New school year / grade level — fee structure must also be new.
          </span>
        )}
      </div>

      {!value.isNew ? (
        <div className={s.formGrid}>
          <div className={`${s.field} ${s.fieldWide}`}>
            <label className={s.fieldLabel}>Existing fee structure for this grade + SY</label>
            <select className={s.fieldSelect}
                    value={value.existingId ?? ""}
                    onChange={e => set("existingId", e.target.value ? Number(e.target.value) : null)}>
              <option value="">— select —</option>
              {matching.map(fs => (
                <option key={fs.id} value={fs.id}>
                  Total {fs.totalAmount} · {fs.months || 9} months
                  {fs.tuitionFee ? ` · Tuition ${fs.tuitionFee}` : ""}
                </option>
              ))}
            </select>
            {matching.length === 0 && (
              <div style={{ marginTop: 6, fontSize: "0.82rem", color: "#b06628" }}>
                No fee structure exists yet for this grade + school year. Switch
                to &quot;Create new&quot; to define one.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={s.formGrid}>
          <Field s={s} label="Tuition fee"  type="number" value={value.tuitionFee}
                 onChange={v => set("tuitionFee", v)} placeholder="14000.00" />
          <Field s={s} label="Miscellaneous" type="number" value={value.miscFee}
                 onChange={v => set("miscFee", v)} placeholder="4500.00" />
          <Field s={s} label="Books fee"    type="number" value={value.booksFee}
                 onChange={v => set("booksFee", v)} placeholder="4000.00" />
          <Field s={s} label="Enrollment fee" type="number" value={value.enrollmentFee}
                 onChange={v => set("enrollmentFee", v)} placeholder="1000.00" />
          <Field s={s} label="Months (installments)" type="number"
                 value={value.months} onChange={v => set("months", v)} placeholder="9" />
          <div className={s.field}>
            <label className={s.fieldLabel}>Total (auto-computed)</label>
            <input className={s.fieldInput} type="text" value={total} readOnly />
          </div>
          <Field s={s} label="Notes" wide value={value.breakdown}
                 onChange={v => set("breakdown", v)} placeholder="Free text" />
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Step 5 · Discounts
// ────────────────────────────────────────────────────────────────
function DiscountsStep({ value, onChange, s, canEdit = true }) {
  function addRow()        { onChange([...value, { ...BLANK_DISCOUNT_ROW }]); }
  function removeRow(i)    { onChange(value.filter((_, idx) => idx !== i)); }
  function patchRow(i, p)  { onChange(value.map((r, idx) => (idx === i ? { ...r, ...p } : r))); }
  return (
    <div>
      {!canEdit && (
        <div className={s.stub}
             style={{ marginBottom: 14, background: "#faf2dd", borderColor: "#e6d7a3" }}>
          <div className={s.stubTitle}>Discounts are Admin-only</div>
          <div>
            Only an Admin or Super Admin can add discounts to an enrolment.
            Finish the rest of the form and ask an admin to add discounts
            afterwards in Operations → Tuition → Discounts.
          </div>
        </div>
      )}
      {value.length === 0 && canEdit && (
        <div className={s.stub} style={{ marginBottom: 14 }}>
          <div className={s.stubTitle}>No discounts</div>
          <div>Optional. Click &quot;+ Add discount&quot; to add one or more.</div>
        </div>
      )}
      {value.map((d, i) => (
        <div key={i} className={s.reviewCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Discount #{i + 1}</h3>
            <button type="button" className={s.btnGhost} onClick={() => removeRow(i)}>Remove</button>
          </div>
          <div className={s.formGrid}>
            <Field s={s} label="Type" type="select"
                   value={d.type}
                   onChange={v => patchRow(i, { type: v, customName: v === "OTHERS" ? d.customName : "" })}
                   options={DISCOUNT_TYPES} />
            <Field s={s} label="Percentage (0-100)" type="number" required
                   value={d.percentage} onChange={v => patchRow(i, { percentage: v })}
                   placeholder="25" />
            {d.type === "OTHERS" && (
              <Field s={s} label="Custom name" required wide
                     value={d.customName}
                     onChange={v => patchRow(i, { customName: v })}
                     placeholder='e.g. "Early Bird", "Staff Child"' />
            )}
            <div className={`${s.field} ${s.fieldWide}`}>
              <label className={s.fieldLabel}>Applies to<span className={s.req}>*</span></label>
              <div style={{ display: "flex", gap: 16, padding: "8px 2px", flexWrap: "wrap" }}>
                {[
                  ["appliesTuition", "Tuition"],
                  ["appliesMisc",    "Miscellaneous"],
                  ["appliesBooks",   "Books"],
                ].map(([key, lbl]) => (
                  <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!d[key]}
                           onChange={e => patchRow(i, { [key]: e.target.checked })} />
                    <span>{lbl}</span>
                  </label>
                ))}
              </div>
            </div>
            <Field s={s} label="Notes" wide
                   value={d.notes} onChange={v => patchRow(i, { notes: v })}
                   placeholder="Free text" />
          </div>
        </div>
      ))}
      {canEdit && (
        <button type="button" className={s.btnGhost} onClick={addRow}>+ Add discount</button>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Step 6 · Documents (multi-row file upload to S3 on save)
// ────────────────────────────────────────────────────────────────
function DocumentsStep({ value, onChange, s }) {
  function addRow()        { onChange([...value, { ...BLANK_DOC_ROW }]); }
  function removeRow(i)    { onChange(value.filter((_, idx) => idx !== i)); }
  function patchRow(i, p)  { onChange(value.map((r, idx) => (idx === i ? { ...r, ...p } : r))); }
  return (
    <div>
      {value.length === 0 && (
        <div className={s.stub} style={{ marginBottom: 14 }}>
          <div className={s.stubTitle}>No documents attached</div>
          <div>Click &quot;+ Add document&quot; to attach files. They upload to AWS S3 on save.</div>
        </div>
      )}
      {value.map((d, i) => (
        <div key={i} className={s.reviewCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Document #{i + 1}</h3>
            <button type="button" className={s.btnGhost} onClick={() => removeRow(i)}>Remove</button>
          </div>
          <div className={s.formGrid}>
            <Field s={s} label="Label"
                   value={d.label} onChange={v => patchRow(i, { label: v })}
                   placeholder="e.g. Birth Certificate" />
            <div className={s.field}>
              <label className={s.fieldLabel}>File</label>
              <input type="file"
                     onChange={e => {
                       const f = e.target.files?.[0] || null;
                       patchRow(i, { file: f, label: d.label || (f?.name || "") });
                     }} />
              {d.file && (
                <div style={{ fontSize: "0.78rem", color: "#8a8070", marginTop: 4 }}>
                  {d.file.name} · {(d.file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <button type="button" className={s.btnGhost} onClick={addRow}>+ Add document</button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Step 7 · Review & Save (with live progress)
// ────────────────────────────────────────────────────────────────
function ReviewStep({
  student, parents, enrollment, tuition, discounts, documents,
  allParents, schoolYears, semesters, gradeLevels, sections, feeStructures,
  progress, busy, s,
}) {
  // Resolve TypeableCombo values to human labels — either the existing
  // row's name, or the "new name" typed in the combo.
  const labelOf = (v, list, field = "label") =>
    v?.id != null ? (list.find(x => x.id === v.id)?.[field] || `#${v.id}`)
    : v?.newName != null ? `${v.newName} (new)`
    : "—";

  const sy    = labelOf(enrollment.schoolYear, schoolYears);
  const sem   = labelOf(enrollment.semester,   semesters);
  const grade = labelOf(enrollment.gradeLevel, gradeLevels, "name");
  const sec   = labelOf(enrollment.section,    sections,    "name");

  const feeLabel = tuition.existingId
    ? (() => {
        const fs = feeStructures.find(x => x.id === tuition.existingId);
        return fs ? `Existing · Total ${fs.totalAmount} · ${fs.months || 9} months`
                  : `#${tuition.existingId}`;
      })()
    : tuition.isNew
    ? `New · Tuition ${tuition.tuitionFee || 0} + Misc ${tuition.miscFee || 0} + Books ${tuition.booksFee || 0}`
    : "—";

  return (
    <>
      {/* Live progress during save */}
      {(busy || Object.keys(progress).length > 0) && (
        <div className={s.reviewCard}>
          <h3>Save progress</h3>
          {[
            ["academic",  "Academic structure (SY · semester · grade · section)"],
            ["tuition",   "Fee structure"],
            ["student",   "Student record"],
            ["parents",   "Parents & links"],
            ["enrollment","Enrollment (auto-creates invoice)"],
            ["discounts", "Discounts"],
            ["documents", "Documents → S3"],
          ].map(([key, label]) => (
            <div key={key} className={s.reviewRow}>
              <span className={s.reviewLabel}>{label}</span>
              <span className={s.reviewValue}>
                <ProgressDot status={progress[key]} />
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={s.reviewCard}>
        <h3>Student</h3>
        <Row s={s} label="Student #"    v={student.studentNumber} />
        <Row s={s} label="Name"         v={`${student.lastName}, ${student.firstName} ${student.middleName || ""}`.trim()} />
        <Row s={s} label="Birthdate"    v={student.birthdate || "—"} />
        <Row s={s} label="Sex"          v={student.sex || "—"} />
        <Row s={s} label="Contact"      v={student.contact || "—"} />
        <Row s={s} label="Address"      v={student.address || "—"} />
      </div>

      <div className={s.reviewCard}>
        <h3>Parents / Guardians ({parents.length})</h3>
        {parents.length === 0 ? (
          <div style={{ fontStyle: "italic", color: "#8a8070" }}>None</div>
        ) : parents.map((p, i) => {
          const existing = allParents.find(x => x.id === p.existingId);
          const name = existing
            ? `${existing.lastName}, ${existing.firstName} (existing)`
            : `${p.lastName || ""}, ${p.firstName || ""}`;
          return (
            <Row key={i} s={s}
                 label={`${p.relationship}${p.primary ? " · Primary" : ""}`}
                 v={name} />
          );
        })}
      </div>

      <div className={s.reviewCard}>
        <h3>Enrollment</h3>
        <Row s={s} label="School year" v={sy} />
        <Row s={s} label="Semester"    v={sem} />
        <Row s={s} label="Grade level" v={grade} />
        <Row s={s} label="Section"     v={sec} />
      </div>

      <div className={s.reviewCard}>
        <h3>Tuition</h3>
        <Row s={s} label="Fee structure" v={feeLabel} />
      </div>

      <div className={s.reviewCard}>
        <h3>Discounts ({discounts.length})</h3>
        {discounts.length === 0 ? (
          <div style={{ fontStyle: "italic", color: "#8a8070" }}>None</div>
        ) : discounts.map((d, i) => {
          const labelBits = [d.type === "OTHERS" && d.customName ? `${d.type} · ${d.customName}` : d.type];
          const scope = [d.appliesTuition && "Tuition", d.appliesMisc && "Misc", d.appliesBooks && "Books"]
                         .filter(Boolean).join(" + ") || "—";
          return (
            <Row key={i} s={s}
                 label={labelBits.join(" ")}
                 v={`${d.percentage || 0}% → ${scope}${d.notes ? " · " + d.notes : ""}`} />
          );
        })}
      </div>

      <div className={s.reviewCard}>
        <h3>Documents ({documents.length})</h3>
        {documents.length === 0 ? (
          <div style={{ fontStyle: "italic", color: "#8a8070" }}>None</div>
        ) : documents.map((d, i) => (
          <Row key={i} s={s} label={d.label || "Document"}
               v={d.file ? `${d.file.name} · ${(d.file.size / 1024).toFixed(1)} KB` : "— no file selected —"} />
        ))}
      </div>
    </>
  );
}

function ProgressDot({ status }) {
  const map = {
    pending: { text: "pending", color: "#8a8070" },
    running: { text: "running…", color: "#b06628" },
    done:    { text: "✓ done",   color: "#2e6a3d" },
    failed:  { text: "✕ failed", color: "#b03030" },
  };
  const hit = map[status] || map.pending;
  return <span style={{ color: hit.color, fontWeight: 600 }}>{hit.text}</span>;
}

function Row({ s, label, v }) {
  return (
    <div className={s.reviewRow}>
      <span className={s.reviewLabel}>{label}</span>
      <span className={s.reviewValue}>{v || "—"}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Shared form field helper
// ────────────────────────────────────────────────────────────────
function Field({ s, label, value, onChange, type = "text", required, placeholder, wide, options, disabled }) {
  return (
    <div className={`${s.field} ${wide ? s.fieldWide : ""}`}>
      <label className={s.fieldLabel}>
        {label}{required && <span className={s.req}>*</span>}
      </label>
      {type === "select" ? (
        <select className={s.fieldSelect}
                value={value ?? ""} disabled={disabled}
                onChange={e => onChange(e.target.value)}>
          {(options || []).map(([v, lbl]) => (
            <option key={v} value={v}>{lbl}</option>
          ))}
        </select>
      ) : (
        <input className={s.fieldInput}
               type={type} value={value ?? ""}
               placeholder={placeholder || ""} disabled={disabled}
               onChange={e => onChange(e.target.value)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// PDF payload builder — resolve combo values to human labels so the
// rendered PDF shows names instead of "{newName: 'Grade 5'}" JSON.
// ────────────────────────────────────────────────────────────────
function buildPdfPayload({
  student, parents, enrollment, tuition, discounts, documents,
  allParents, schoolYears, semesters, gradeLevels, sections,
}) {
  const labelOf = (v, list, field = "label") =>
    v?.id != null ? (list.find(x => x.id === v.id)?.[field] || "")
    : v?.newName != null ? `${v.newName} (new)` : "";

  const n = (x) => (x === "" || x == null ? 0 : Number(x) || 0);
  const total = (n(tuition.tuitionFee) + n(tuition.miscFee) + n(tuition.booksFee)).toFixed(2);

  return {
    student,
    parents: parents.map(p => {
      const ex = allParents.find(x => x.id === p.existingId);
      return ex
        ? { firstName: ex.firstName, lastName: ex.lastName,
            contact: ex.contact, email: ex.email, occupation: ex.occupation,
            relationship: p.relationship, primary: p.primary }
        : p;
    }),
    enrollment: {
      schoolYearLabel: labelOf(enrollment.schoolYear, schoolYears),
      semesterLabel:   labelOf(enrollment.semester,   semesters),
      gradeLevelName:  labelOf(enrollment.gradeLevel, gradeLevels, "name"),
      sectionName:     labelOf(enrollment.section,    sections,    "name"),
      status: "ENROLLED",
    },
    tuition: tuition.isNew
      ? { ...tuition, totalAmount: total }
      : { existingId: tuition.existingId },
    discounts,
    documents: documents.map(d => ({
      label: d.label, originalFilename: d.file?.name,
    })),
  };
}
